import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { buildOptions } from "../../utils/formHelper";
import { BasicFormikForm } from "../form/BasicFormikForm";
import { BaseBudgetInputFilterForm } from "./BaseBudgetInputFilterForm";
import { DataService } from "../../services/DataService";

export const BaseBudgetInputFilterSection = ({
  filters,
  budgetParameters,
  onFilterSubmit,
  showFiscalDetails,
}) => {
  const dataService = new DataService();

  // Validate dependsOn references
  filters.forEach((filter) => {
    if (filter.dependsOn && !filters.find((f) => f.key === filter.dependsOn)) {
      throw new Error(
        `Filter ${filter.key} depends on non-existent filter ${filter.dependsOn}`,
      );
    }
  });

  const [parentValues, setParentValues] = useState({});
  const [options, setOptions] = useState(
    filters.reduce((acc, filter) => ({ ...acc, [filter.key]: [] }), {}),
  );
  const genericQueryParams = {
    pagination: false,
    active: 1,
    "order[name]": "ASC",
    "groups[]": "select:get",
  };

  // Build validation schema from filters
  const validationSchema = Yup.object(
    filters.reduce(
      (acc, filter) => ({
        ...acc,
        [filter.key]: filter.dependsOn
          ? filter.allowMultiple
            ? Yup.array()
                .when(filter.dependsOn, {
                  is: (val) => Boolean(val),
                  then: () => Yup.array().min(1, `${filter.key} is required`),
                  otherwise: () => Yup.array(),
                })
                .label(filter.label)
            : Yup.string()
                .when(filter.dependsOn, {
                  is: (val) => Boolean(val),
                  then: () =>
                    Yup.string().required(`${filter.key} is required`),
                  otherwise: () => Yup.string(),
                })
                .label(filter.label)
          : filter.allowMultiple
          ? Yup.array().min(1, `${filter.key} is required`).label(filter.label)
          : Yup.string()
              .required(`${filter.key} is required`)
              .label(filter.label),
      }),
      {},
    ),
  );

  // Build initial values from filters
  const initialValues = filters.reduce(
    (acc, filter) => ({
      ...acc,
      [filter.key]: filter.allowMultiple
        ? filter.defaultValue
          ? Array.isArray(filter.defaultValue)
            ? filter.defaultValue
            : [filter.defaultValue]
          : []
        : filter.defaultValue || "",
    }),
    {},
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      // Format values properly for API request
      const formattedValues = {};

      // Process each value
      Object.entries(values).forEach(([key, value]) => {
        const filter = filters.find((f) => f.key === key);

        // Skip empty arrays
        if (
          filter?.allowMultiple &&
          Array.isArray(value) &&
          value.length === 0
        ) {
          return;
        }

        // Include all other values (both single and multiple)
        formattedValues[key] = value;
      });

      onFilterSubmit(formattedValues);
    },
  });

  // Dynamic special configuration based on filters
  formik["special"] = {
    customStructure: {
      0: filters.map((filter) => filter.key),
    },
    layout: {
      ...filters.reduce(
        (acc, filter) => ({
          ...acc,
          [filter.key]: {
            selectWidth: filter.width || "15%",
            disabled: filter.dependsOn
              ? !formik.values[filter.dependsOn]
              : false,
            ...(filter.layoutProps || {}),
          },
        }),
        {},
      ),
      lineDisposition: "start",
      childrens: (
        <BaseBudgetInputFilterForm
          budgetParameters={budgetParameters}
          formik={formik}
          showFiscalDetails={showFiscalDetails}
        />
      ),
    },
    multipleSelect: filters.reduce((acc, filter) => {
      if (filter.allowMultiple) {
        return { ...acc, [filter.key]: true };
      }
      return acc;
    }, {}),
	placeholder: filters.reduce((acc, filter) => {
	  if (filter.placeholder) {
		return { ...acc, [filter.key]: filter.placeholder };
	  }
	  return acc;
	}, {}),
  };

  const hasParentValueChanged = (filter) => {
    const parentValue = formik.values[filter.dependsOn];
    const previousParentValue = parentValues[filter.dependsOn];

    if (filter.allowMultiple) {
      // Handle array comparison for multiple values
      if (Array.isArray(parentValue) && Array.isArray(previousParentValue)) {
        return (
          parentValue.length > 0 &&
          (parentValue.length !== previousParentValue.length ||
            !parentValue.every((v) => previousParentValue.includes(v)))
        );
      }
    }

    return parentValue && parentValue !== previousParentValue;
  };

  useEffect(() => {
    filters.forEach((filter) => {
      let optionsAreEmpty = options[filter.key].length === 0;

      // Handle dependent filters
      if (filter.dependsOn) {
        const parentValue = formik.values[filter.dependsOn];

        // If parent value changed
        if (hasParentValueChanged(filter)) {
          // Update parent value tracking and reset dependent filter
          setParentValues((prev) => ({
            ...prev,
            [filter.dependsOn]: parentValue,
          }));
          formik.setFieldValue(filter.key, filter.allowMultiple ? [] : "");
          setOptions((prev) => ({
            ...prev,
            [filter.key]: [],
          }));
          optionsAreEmpty = true;
        }

        // Load options for dependent filter if parent has value
        if (
          parentValue &&
          (Array.isArray(parentValue) ? parentValue.length > 0 : true)
        ) {
          const shouldReload = optionsAreEmpty;

          if (shouldReload) {
            const queryParams = {
              ...genericQueryParams,
              ...(filter.queryParams || {}),
            };

            // Handle parent value(s) correctly
            const parentFilter = filters.find(
              (f) => f.key === filter.dependsOn,
            );

            // Get the parameter name to use for sending the parent value
            const paramName = filter.dependsOnPayloadName || filter.dependsOn;

            if (parentFilter?.allowMultiple && Array.isArray(parentValue)) {
              // For multiple parent values, use the format paramName[]=value
              parentValue.forEach((val) => {
                if (!queryParams[`${paramName}[]`]) {
                  queryParams[`${paramName}[]`] = [];
                }
                queryParams[`${paramName}[]`].push(val);
              });
            } else {
              queryParams[paramName] = parentValue;
            }

            dataService.fetchData(
              filter.route,
              queryParams,
              (data) => {
                setOptions((prev) => ({
                  ...prev,
                  [filter.key]: buildOptions(data, "name", "id"),
                }));
                if (filter.onDataLoad) {
                  filter.onDataLoad(data);
                }
              },
              { errorMessage: `Unable to retrieve ${filter.key}` },
            );
          }
        }
      }
      // Handle independent filters
      else if (options[filter.key].length === 0) {
        const queryParams = {
          ...genericQueryParams,
          ...(filter.queryParams || {}),
        };

        dataService.fetchData(
          filter.route,
          queryParams,
          (data) => {
            setOptions((prev) => ({
              ...prev,
              [filter.key]: buildOptions(data, "name", "id"),
            }));
            if (filter.onDataLoad) {
              filter.onDataLoad(data);
            }
          },
          { errorMessage: `Unable to retrieve ${filter.key}` },
        );
      }
    });
  }, [formik.values]);

  return filters.length > 0 ? (
    <BasicFormikForm
      formik={formik}
      validations={validationSchema}
      options={options}
    />
  ) : null;
};
